import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CharacterCard, CharacterCardSkeleton } from '../components/CharacterCard';
import AdSlot from '../components/AdSlot';
import { Sparkles, Wand2, Gamepad2, Scroll, Mic, UserPlus, Star, TrendingUp, Heart, Rocket, Skull, Laugh, Flame, Bell, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { initPushNotifications, isPushEnabled } from '../utils/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  all: Sparkles, anime: Star, fantasy: Wand2, games: Gamepad2, historical: Scroll,
  celebrities: Mic, custom: UserPlus, romance: Heart, scifi: Rocket, horror: Skull, comedy: Laugh,
};

export default function Home() {
  const { user } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [showPushBanner, setShowPushBanner] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchCharacters();
    fetchTrending();
    
    // Auto-refresh trending every 2 minutes
    const trendingInterval = setInterval(fetchTrending, 120000);
    return () => clearInterval(trendingInterval);
  }, []);

  // Check if we should show push notification banner
  useEffect(() => {
    if (user && 'PushManager' in window) {
      const dismissed = localStorage.getItem('push_banner_dismissed');
      if (!dismissed) {
        isPushEnabled().then(enabled => {
          if (!enabled) setShowPushBanner(true);
        });
      }
    }
  }, [user]);

  const enablePush = async () => {
    const success = await initPushNotifications();
    if (success) {
      setShowPushBanner(false);
      localStorage.setItem('push_banner_dismissed', 'true');
    }
  };

  const dismissPushBanner = () => {
    setShowPushBanner(false);
    localStorage.setItem('push_banner_dismissed', 'true');
  };

  useEffect(() => {
    fetchCharacters();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/categories`);
      setCategories(data);
    } catch (error) { console.error('Error fetching categories:', error); }
  };

  const fetchCharacters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      const { data } = await axios.get(`${API_URL}/api/characters?${params.toString()}`);
      setCharacters(data);
    } catch (error) { console.error('Error fetching characters:', error); }
    finally { setLoading(false); }
  };

  const fetchTrending = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/trending`);
      setTrending(data);
    } catch (error) { console.error('Error fetching trending:', error); }
    finally { setTrendingLoading(false); }
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <div className="max-w-7xl mx-auto">
      {/* Push Notification Banner */}
      {showPushBanner && (
        <div className="mx-4 lg:mx-6 mt-3 p-3 bg-gradient-to-r from-[#E11D48]/10 to-[#F59E0B]/10 border border-[#E11D48]/20 rounded-xl flex items-center gap-3" data-testid="push-banner">
          <Bell className="w-5 h-5 text-[#E11D48] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white">Enable notifications</p>
            <p className="text-[10px] text-[#71717A]">Get notified about gem gifts, chats & updates</p>
          </div>
          <button onClick={enablePush} className="px-3 py-1.5 bg-[#E11D48] text-white text-xs font-medium rounded-full hover:bg-[#BE123C] transition-colors" data-testid="enable-push-btn">
            Enable
          </button>
          <button onClick={dismissPushBanner} className="p-1 text-[#71717A] hover:text-white" data-testid="dismiss-push-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Trending This Week */}
      {trending.length > 0 && (
        <section className="px-4 lg:px-6 pt-4 lg:pt-8 pb-2">
          <div className="flex items-center gap-2 mb-3 lg:mb-5">
            <div className="p-1.5 bg-gradient-to-br from-[#E11D48] to-[#F59E0B] rounded-lg">
              <Flame className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
            </div>
            <h2 className="text-sm lg:text-lg font-bold text-white font-['Unbounded']">Trending This Week</h2>
          </div>
          <div className="flex gap-3 lg:gap-5 overflow-x-auto scrollbar-hide pb-2 lg:overflow-visible">
            {trending.slice(0, 8).map((character) => (
              <Link
                key={`trending-${character.id}`}
                to={`/character/${character.id}`}
                className="flex-shrink-0 w-28 lg:w-36 group"
                data-testid={`trending-${character.id}`}
              >
                <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-xl overflow-hidden border-2 border-[#E11D48]/30 mb-1.5 group-hover:border-[#E11D48] transition-colors">
                  <img
                    src={character.avatar_url || ''}
                    alt={character.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.target.style.background = '#1A1D24'; }}
                  />
                </div>
                <p className="text-xs lg:text-sm text-white font-medium line-clamp-1 text-center">{character.name}</p>
                <p className="text-[10px] lg:text-xs text-[#71717A] text-center">{character.chat_count || 0} chats</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      <div className="sticky top-[calc(env(safe-area-inset-top)+3rem)] lg:top-0 z-40 bg-[#050505]/95 backdrop-blur-sm">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4 lg:px-6 py-3 lg:py-4">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 min-h-[40px] rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              selectedCategory === 'all' ? 'bg-white text-black' : 'bg-[#1A1D24] text-[#A1A1AA] hover:text-white hover:bg-[#23262E]'
            }`}
            data-testid="category-for-you"
          >
            <TrendingUp className="w-4 h-4" /> For You
          </button>
          {categories.filter(c => c.id !== 'all').map((category) => {
            const Icon = categoryIcons[category.id] || Sparkles;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 min-h-[40px] rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category.id ? 'bg-white text-black' : 'bg-[#1A1D24] text-[#A1A1AA] hover:text-white hover:bg-[#23262E]'
                }`}
                data-testid={`category-${category.id}`}
              >
                <Icon className="w-4 h-4" /> {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Characters Grid */}
      <section className="px-3 lg:px-6 py-4 lg:py-6">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 lg:gap-5">
            {[...Array(12)].map((_, i) => <CharacterCardSkeleton key={i} />)}
          </div>
        ) : characters.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-10 h-10 text-[#71717A] mx-auto mb-3" />
            <h3 className="text-base font-medium text-white mb-2">No characters found</h3>
            <p className="text-sm text-[#71717A]">Try a different category</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 lg:gap-5">
            {characters.map((character, index) => {
              const showAdAfter = (index + 1) % 12 === 0 && index < characters.length - 1;
              return [
                <div key={character.id} className="stagger-item" style={{ animationDelay: `${index * 0.03}s` }}>
                  <CharacterCard character={character} />
                </div>,
                showAdAfter && (
                  <AdSlot key={`ad-${character.id}`} variant="native-card" className="stagger-item" />
                ),
              ];
            })}
          </div>
        )}
      </section>
      </div>
    </div>
  );
}
